// Pro.jsx — Barakah Budget Pro/Free system
// Free:  transactions, overview, calendar, basic zakat, view/export/import backup,
//        3 goals, recurring (view only)
// Pro:   AI chat, AI goal coaching, debt tracker, net worth, manage recurring,
//        live nisab, full zakat history, budget planning

import { useState } from "react";
import { createPortal } from "react-dom";
import { logInfo, logWarn } from "./logger";
import { C } from "./constants";

// ── Payment links ────────────────────────────────────────────
const STRIPE_MONTHLY = "https://buy.stripe.com/test_4gMaEW63udmy4Wf9d87AI00";
const STRIPE_YEARLY  = "https://buy.stripe.com/test_3cIfZgdvW6YabkDexs7AI01";

// ── Pro state helpers ────────────────────────────────────────
// isPro() checks BOTH the flag AND that a token exists.
// Setting bb_pro=true in console does nothing without a bb_pro_token.
export const isPro = () =>
  localStorage.getItem("bb_pro") === "true" &&
  !!localStorage.getItem("bb_pro_token");

export const setPro = (v) => localStorage.setItem("bb_pro", v ? "true" : "false");

// Verify pro token with server
// - On first load: always verify
// - After that: re-verify every 7 days
// - On network error: trust local cache (offline support)
export async function verifyProToken() {
  const token = localStorage.getItem("bb_pro_token");

  // No token = definitely not Pro (even if bb_pro=true was manually set)
  if (!token) {
    logInfo("auth", "verify_skipped", { reason: "no_token" });
    localStorage.setItem("bb_pro", "false");
    return false;
  }

  // Check if we verified recently (within 7 days)
  const lastVerified = parseInt(localStorage.getItem("bb_pro_verified_at") || "0");
  const daysSince    = (Date.now() - lastVerified) / (1000 * 60 * 60 * 24);
  if (daysSince < 7 && localStorage.getItem("bb_pro") === "true") {
    return true; // Cache still fresh — trust it
  }

  // Re-verify with server (checks Stripe subscription is still active)
  try {
    const res  = await fetch(`/api/verify-pro?token=${encodeURIComponent(token)}`);
    const data = await res.json();
    if (data.valid) {
      logInfo("auth", "verify_success", { cached: false });
      localStorage.setItem("bb_pro", "true");
      localStorage.setItem("bb_pro_verified_at", Date.now().toString());
      return true;
    } else {
      logWarn("auth", "verify_failed", { reason: data.reason || "unknown" });
      localStorage.removeItem("bb_pro_token");
      localStorage.removeItem("bb_pro_verified_at");
      localStorage.setItem("bb_pro", "false");
      return false;
    }
  } catch (err) {
    logWarn("auth", "verify_network_error", { error: err.message, usingCache: true });
    return localStorage.getItem("bb_pro") === "true" && !!token;
  }
}

// Handle successful payment redirect (?success=1&session_id=xxx)
export async function handleStripeSuccess(sessionId) {
  try {
    const res  = await fetch("/api/verify-pro", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ sessionId }),
    });
    const data = await res.json();
    if (data.token) {
      localStorage.setItem("bb_pro_token", data.token);
      localStorage.setItem("bb_pro", "true");
      localStorage.setItem("bb_pro_verified_at", Date.now().toString());
      return true;
    }
  } catch {}
  return false;
}

// ── PRO FEATURES MAP ─────────────────────────────────────────
export const PRO_FEATURES = {
  ai_chat:        { label: "AI Financial Coach",     emoji: "🤖", desc: "Ask Claude anything about your finances, halal-aware" },
  goals_unlimited:{ label: "AI Goal Coaching",       emoji: "🎯", desc: "Goals are free — Pro adds AI milestone coaching on each one" },
  debt_tracker:   { label: "Debt Tracker",           emoji: "💳", desc: "Halal payoff strategies, no riba encouragement" },
  net_worth:      { label: "Net Worth",              emoji: "📈", desc: "See your full financial picture over time" },
  recurring_manage:{ label:"Recurring Manager",      emoji: "🔁", desc: "Add, edit and manage recurring bills" },
  export_backup:  { label: "Export & Backup",        emoji: "📤", desc: "Download your full data as a JSON backup file" },
  live_nisab:     { label: "Live Nisab Price",       emoji: "🌙", desc: "Real-time gold price for accurate Zakat" },
  budget_edit:    { label: "Budget Planning",        emoji: "⚙️",  desc: "Set income allocations and track spending against your monthly plan" },
  // niyyah_jars + ramadan_mode: coming in future release
  zakat_history:  { label: "Zakat History",          emoji: "📋", desc: "Full record of what you paid, when, to whom" },
};

// ── UPGRADE MODAL ─────────────────────────────────────────────
export function UpgradeModal({ onClose, trigger = "general" }) {
  const [plan, setPlan] = useState("yearly");

  // Contextual headline based on what triggered the modal
  const headlines = {
    ai_chat:         { title: "Meet your AI Finance Coach", sub: "Ask anything — halal debt payoff, Hajj savings, spending analysis. Powered by Claude." },
    goals_unlimited: { title: "Unlock Unlimited Goals",     sub: "You've got the saving habit. Now track every dream — Hajj, wedding, emergency fund, and more." },
    debt_tracker:    { title: "Take Control of Your Debt",  sub: "Halal payoff strategies, zero riba advice. Know exactly when you'll be free." },
    net_worth:       { title: "See Your Full Picture",      sub: "Assets minus liabilities. Watch your net worth grow month by month." },
    recurring_manage:{ title: "Master Your Bills",          sub: "Add, edit, and manage all recurring expenses in one place." },
    general:         { title: "Upgrade to Barakah Pro",     sub: "Your personal Islamic finance coach. Less than one coffee a month." },
  };

  const { title, sub } = headlines[trigger] || headlines.general;

  const proList = [
    // AI items hidden until native app feedback confirms demand
    // ["🤖", "AI Financial Coach — ask Claude anything"],
    // ["🎯", "AI coaching on every savings goal"],
    ["💳", "Debt tracker with halal payoff plans"],
    ["📈", "Net worth tracking over time"],
    ["🌙", "Live Nisab + full Zakat history"],
    ["🔁", "Manage recurring bills"],
    ["⚙️", "Budget planning & income allocations"],
  ];

  return createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:3000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
      backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{ background:C.surface, borderRadius:24, padding:"24px 20px",
        maxWidth:480, width:"100%", animation:"fadeUp 0.3s ease",
        border:"1px solid rgba(167,139,250,0.25)",
        maxHeight:"90vh", overflowY:"auto", WebkitOverflowScrolling:"touch" }}>

        {/* Close */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:C.text, letterSpacing:"-0.03em", lineHeight:1.2 }}>{title}</div>
            <div style={{ fontSize:13, color:C.text2, marginTop:6, lineHeight:1.5 }}>{sub}</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.06)", border:"none",
            color:C.text3, borderRadius:50, width:30, height:30, fontSize:18, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:12 }}>×</button>
        </div>

        {/* Plan toggle */}
        <div style={{ display:"flex", background:C.surface2, borderRadius:12, padding:3, marginBottom:20, border:`1px solid ${C.border}` }}>
          {[
            { id:"monthly", label:"Monthly", price:"CA$4.99/mo" },
            { id:"yearly",  label:"Yearly",  price:"CA$39.99/yr", badge:"Save 33%" },
          ].map(p => (
            <button key={p.id} onClick={() => setPlan(p.id)}
              style={{ flex:1, padding:"10px 8px", borderRadius:10, border:"none", cursor:"pointer",
                background: plan===p.id ? C.purple : "transparent",
                color: plan===p.id ? "#fff" : C.text3, transition:"all 0.2s" }}>
              <div style={{ fontSize:12, fontWeight:700 }}>{p.label}</div>
              <div style={{ fontSize:11, opacity:0.85 }}>{p.price}</div>
              {p.badge && plan===p.id && (
                <div style={{ fontSize:9, fontWeight:700, background:"rgba(255,255,255,0.2)",
                  borderRadius:4, padding:"1px 5px", marginTop:2, display:"inline-block" }}>{p.badge}</div>
              )}
            </button>
          ))}
        </div>

        {/* Feature list — compact 2 col */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px", marginBottom:22 }}>
          {proList.map(([emoji, text], i) => (
            <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:7 }}>
              <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{emoji}</span>
              <span style={{ fontSize:11, color:C.text2, lineHeight:1.4 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={() => {
            const link = plan === "monthly" ? STRIPE_MONTHLY : STRIPE_YEARLY;
            window.location.href = link;
          }}
          style={{ width:"100%", padding:"16px", borderRadius:14, border:"none", cursor:"pointer",
            background:"linear-gradient(135deg, #a78bfa, #7c3aed)", color:"#fff",
            fontSize:15, fontWeight:700, letterSpacing:"-0.01em" }}>
          {plan === "monthly" ? "Start Pro — CA$4.99/month" : "Start Pro — CA$39.99/year"}
        </button>

        <div style={{ textAlign:"center", marginTop:10, fontSize:11, color:C.text3 }}>
          Cancel anytime · Your data stays forever · No interest, ever
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── PRO GATE WRAPPER ──────────────────────────────────────────
// Wraps a feature. If not pro, shows a blurred preview + upgrade card.
export function ProGate({ feature, children, compact = false }) {
  const [showModal, setShowModal] = useState(false);
  const pro = isPro();

  if (pro) return children;

  const info = PRO_FEATURES[feature] || {};

  if (compact) {
    // Small inline lock badge — for sub-items
    return (
      <>
        <div style={{ position:"relative", pointerEvents:"none", opacity:0.35, userSelect:"none",
          filter:"blur(2px)", overflow:"hidden", maxHeight:120 }}>
          {children}
        </div>
        <button onClick={() => setShowModal(true)}
          style={{ width:"100%", marginTop:8, padding:"10px", borderRadius:10,
            background:"rgba(167,139,250,0.1)", border:"1px solid rgba(167,139,250,0.25)",
            color:C.purple, fontSize:12, fontWeight:600, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
          ✨ {info.label || "Pro Feature"} — Upgrade to unlock
        </button>
        {showModal && <UpgradeModal onClose={() => setShowModal(false)} trigger={feature} />}
      </>
    );
  }

  // Full card lock — centered overlay card, not position:absolute (avoids clipping)
  return (
    <>
      {/* Blurred preview behind */}
      <div style={{ pointerEvents:"none", userSelect:"none",
        filter:"blur(4px)", opacity:0.2, maxHeight:160, overflow:"hidden", marginBottom:0 }}>
        {children}
      </div>
      {/* Upgrade card sits below as a normal flow element — never clipped */}
      <div style={{ marginTop:-40, background:"linear-gradient(to bottom, rgba(14,14,19,0) 0%, rgba(14,14,19,0.98) 30%)",
        paddingTop:50, paddingBottom:8, textAlign:"center" }}>
        <div style={{ fontSize:30, marginBottom:8 }}>{info.emoji || "✨"}</div>
        <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>{info.label}</div>
        <div style={{ fontSize:12, color:C.text2, marginBottom:16, lineHeight:1.5, maxWidth:260, margin:"0 auto 16px" }}>{info.desc}</div>
        <button onClick={() => setShowModal(true)}
          style={{ padding:"12px 32px", borderRadius:12, border:"none", cursor:"pointer",
            background:"linear-gradient(135deg, #a78bfa, #7c3aed)",
            color:"#fff", fontSize:13, fontWeight:700 }}>
          Unlock with Pro
        </button>
      </div>
      {showModal && <UpgradeModal onClose={() => setShowModal(false)} trigger={feature} />}
    </>
  );
}

// ── CONTEXTUAL UPGRADE NUDGES ────────────────────────────────
// Call these at the right moments to show soft upgrade prompts

export function useUpgradeNudge() {
  const [nudge, setNudge] = useState(null); // { trigger, message }
  const [showModal, setShowModal] = useState(false);
  const [modalTrigger, setModalTrigger] = useState("general");

  const checkNudges = ({ txCount, goalCount }) => {
    if (isPro()) return;
    const shownKey = "bb_nudges_shown";
    const shown = JSON.parse(localStorage.getItem(shownKey) || "{}");

    if (txCount >= 30 && !shown.month_streak) {
      shown.month_streak = true;
      localStorage.setItem(shownKey, JSON.stringify(shown));
      // hidden until native app feedback confirms demand
      // setNudge({ trigger:"ai_chat", message:"📊 30 transactions tracked! Unlock AI insights to see what your spending says about you." });
    } else if (goalCount >= 3 && !shown.goals_limit) {
      shown.goals_limit = true;
      localStorage.setItem(shownKey, JSON.stringify(shown));
      setNudge({ trigger:"goals_unlimited", message:"🎯 You've set 3 goals! Pro removes the limit and adds AI coaching for each one." });
    }
  };

  const openUpgrade = (trigger = "general") => {
    setModalTrigger(trigger);
    setShowModal(true);
  };

  const NudgeBanner = nudge ? () => (
    <div style={{ margin:"0 0 14px", padding:"12px 16px", borderRadius:12,
      background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.2)",
      display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ flex:1, fontSize:12, color:C.text2, lineHeight:1.5 }}>{nudge.message}</div>
      <button onClick={() => openUpgrade(nudge.trigger)}
        style={{ background:C.purple, border:"none", color:"#fff", borderRadius:8,
          padding:"7px 12px", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
        Upgrade
      </button>
      <button onClick={() => setNudge(null)}
        style={{ background:"none", border:"none", color:C.text3, cursor:"pointer", fontSize:16, padding:"0 2px" }}>×</button>
    </div>
  ) : () => null;

  const ModalIfOpen = showModal ? () => (
    <UpgradeModal onClose={() => setShowModal(false)} trigger={modalTrigger} />
  ) : () => null;

  return { checkNudges, openUpgrade, NudgeBanner, ModalIfOpen };
}

// ── PRO BADGE ────────────────────────────────────────────────
export function ProBadge() {
  if (!isPro()) return null;
  return (
    <span style={{ background:"linear-gradient(135deg, #a78bfa, #7c3aed)",
      color:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.06em",
      padding:"2px 7px", borderRadius:20, verticalAlign:"middle" }}>PRO</span>
  );
}